/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
interface Args {}

type NotificationsService = {
  notifyConfirmation(message: string): void;
  notifyResponseError(error: Error, options?: { default: string }): void;
};

export default class StandaloneSalesforceSetupInAppMessagingConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: NotificationsService;
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked developerName?: string;
  @tracked url?: string;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    if (this.salesforceConfig.isPendingSetup) {
      return;
    }

    let { deploymentId, hostname } = this.salesforceInAppMessagingConfig || {};
    this.developerName = deploymentId || undefined;
    this.url = hostname || undefined;
  }

  get salesforceConfig() {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceInAppMessagingConfig() {
    return this.salesforcePlatform.salesforceInAppMessagingConfig;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get isConnected(): boolean {
    return this.salesforceInAppMessagingConfig?.isConnected ?? false;
  }

  get isLive(): boolean {
    return this.salesforcePlatform.intercomMessengerConfig?.isMessengerEnabled ?? false;
  }

  get isFormValid(): boolean {
    if (!this.developerName || !this.url) {
      return false;
    }

    try {
      let parsedUrl = new URL(this.url);
      if (parsedUrl.protocol !== 'https:') {
        return false;
      }
      if (!parsedUrl.hostname.endsWith('.my.salesforce-scrt.com')) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  get isRevokeRunning(): boolean {
    return taskFor(this.revoke).isRunning;
  }

  get isConnectRunning(): boolean {
    return taskFor(this.connect).isRunning;
  }

  @restartableTask
  *connect(): TaskGenerator<void> {
    try {
      let config = this.store.createRecord('standalone/salesforce-in-app-messaging-configuration', {
        hostname: this.url,
        deploymentId: this.developerName,
        organizationId: this.salesforcePlatform.salesforceConfig!.organisationId,
      });
      yield config.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.save-success'),
      );
      this.salesforcePlatform.salesforceInAppMessagingConfig = config;
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.save-error'),
      });
    }
  }

  @dropTask
  *revoke(): TaskGenerator<void> {
    try {
      yield this.salesforceInAppMessagingConfig?.revoke();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.revoke-success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.revoke-error'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::InAppMessaging::Connect': typeof StandaloneSalesforceSetupInAppMessagingConnect;
    'standalone/salesforce/setup/in-app-messaging/connect': typeof StandaloneSalesforceSetupInAppMessagingConnect;
  }
}
